import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    getLocalDevices,
    handleEvent, JazzSdk,
    LOCAL_MEDIA_DEVICE_KIND, LocalAudioInputDevice, LocalDevicesEventAudioInputChanged,
    LocalDevicesEventMediaDevicesChanged
} from '@salutejs/jazz-sdk-web';
import { useRoomContext } from '../../contexts/RoomContext';
import { handleError } from '../../utils/handleError';

const isBluetoothDevice = (device) => {
    return device.label.toLowerCase().includes('bluetooth');
}
const isSpeakerDevice = (device) => {
    return device.label.toLowerCase().includes('speaker');
}
export const useBluetoothAudio = () => {
    const { sdk, room } = useRoomContext()
    const [audioInputDevices, setAudioInputDevices] = useState([]);
    const [isBluetoothAudioOn, setIsBluetoothAudioOn] = useState<boolean>(false);

    const localDevices = useMemo(() => {
        if (!sdk) return undefined;
        return getLocalDevices(sdk as JazzSdk);
    }, [sdk]);

    useEffect(() => {
        if (!localDevices) return;
        setAudioInputDevices(localDevices.audioInputDevices.get());

        const unsubscribeDevicesChanged = handleEvent(localDevices.event$, 'mediaDevicesChanged', onMediaDeviceChanged);
        const unsubscribeAudioInputChanged = handleEvent(localDevices.event$, 'audioInputChanged', onAudioInputChanged);

        return () => {
            unsubscribeDevicesChanged();
            unsubscribeAudioInputChanged();
        };
    }, [localDevices]);

    const hasBluetoothAudioDevice = useMemo(() => {
        return audioInputDevices.some((device) => isBluetoothDevice(device));
    }, [audioInputDevices]);

    useEffect(() => {
        if (hasBluetoothAudioDevice) {
            toggleBluetoothAudio();
        }
    }, [hasBluetoothAudioDevice])

    const onMediaDeviceChanged = useCallback(({ payload: { groupedDevices } }: LocalDevicesEventMediaDevicesChanged) => {
        const audioInputDevices = groupedDevices[LOCAL_MEDIA_DEVICE_KIND.AUDIO_INPUT];
        setAudioInputDevices(audioInputDevices);
    }, []);

    const onAudioInputChanged = useCallback(({ payload: { device } }: LocalDevicesEventAudioInputChanged) => {
        setIsBluetoothAudioOn(isBluetoothDevice(device));
    }, []);

    const toggleBluetoothAudio = useCallback(() => {
        if (audioInputDevices.length === 0) {
            return;
        }
        const currentDevice: LocalAudioInputDevice = localDevices.audioInput.get();
        const bluetoothDevice = audioInputDevices.find((device) => isBluetoothDevice(device));
        const speakerDevice = audioInputDevices.find((device) => isSpeakerDevice(device));
        const nextDevice = !isBluetoothDevice(currentDevice) && bluetoothDevice ? bluetoothDevice : speakerDevice;
        if (!nextDevice) {
            throw new Error('Ошибка при поиске аудио-устройства')
        }
        if (nextDevice.deviceId !== currentDevice.deviceId) {
            localDevices
                .selectAudioInput(nextDevice)
                .catch((err) => {
                    handleError(err, 'Ошибка при смене аудио-устройства')
                })
        }
    }, [audioInputDevices]);

    return {
        toggleBluetoothAudio,
        hasBluetoothAudioDevice,
        isBluetoothAudioOn
    }
}
